/**
 * Business Info tab — extracted from the Chatbot page.
 *
 * Stores `project.businessProfile` via PUT /api/v1/projects/:id/business-profile.
 * The AI chatbot's system prompt reads this field directly so admins can
 * fix a wrong / missing address without re-training the knowledge base.
 *
 * Why this lives under the Chatbot page (not project Settings) for v1:
 *   - The bot is the primary consumer today, and admins who care about
 *     contact-accuracy land here when they're debugging "the bot doesn't
 *     know my address."
 *   - The underlying field is project-scoped, so future consumers
 *     (contact page, email signatures, structured-data emission) can read
 *     it without a schema move.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, Save, MapPin, AlertCircle, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { isVersionConflict, conflictCurrentVersion, apiErrorMessage } from '@/lib/conflict';
import { RefreshingHint } from '@/components/common/DataState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface Location {
  name: string;
  address: string;
  phone?: string;
  email?: string;
  hours?: string;
}

interface BusinessProfile {
  legalName?: string;
  displayName?: string;
  description?: string;
  primaryAddress?: string;
  primaryPhone?: string;
  primaryEmail?: string;
  additionalPhones?: string[];
  additionalEmails?: string[];
  hours?: string;
  locations?: Location[];
  socials?: {
    website?: string;
    instagram?: string;
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    youtube?: string;
    whatsapp?: string;
  };
}

const EMPTY: BusinessProfile = {
  legalName: '',
  displayName: '',
  description: '',
  primaryAddress: '',
  primaryPhone: '',
  primaryEmail: '',
  additionalPhones: [],
  additionalEmails: [],
  hours: '',
  locations: [],
  socials: {},
};

export function BusinessInfoTab({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<BusinessProfile>(EMPTY);
  const [dirty, setDirty] = useState(false);
  // Set when the server rejects a save because the profile changed under us
  // (409 VERSION_CONFLICT). Holds the server's current version so we can tell
  // the admin and offer a deliberate overwrite vs. discard-and-reload.
  const [conflict, setConflict] = useState<{ serverVersion: number } | null>(null);

  const { data, isPending, isFetching } = useQuery({
    queryKey: qk.chatbotBusinessInfo(projectId),
    queryFn: async () => {
      const res = await api.get<{
        success: boolean;
        data: { businessProfile: BusinessProfile | null; businessProfileVersion?: number };
      }>(`/projects/${projectId}/business-profile`);
      return {
        businessProfile: res.data?.data?.businessProfile ?? null,
        version: res.data?.data?.businessProfileVersion ?? 0,
      };
    },
  });
  const loadedVersion = data?.version ?? 0;

  // Seed the local draft once when the query lands. The `!dirty` guard means a
  // background refetch never clobbers unsaved edits.
  useEffect(() => {
    if (!isPending && !dirty) {
      setDraft({ ...EMPTY, ...(data?.businessProfile ?? {}) });
    }
  }, [isPending, data, dirty]);

  const save = useMutation({
    mutationFn: async (opts: { payload: BusinessProfile; force?: boolean }) => {
      // Optimistic concurrency: declare the version we loaded so the server
      // rejects (409) if someone else saved in the meantime. `force` — chosen
      // by the admin after acknowledging a conflict — omits the guard to
      // deliberately overwrite the newer server state.
      const res = await api.put<{ success: boolean; data: { businessProfile: BusinessProfile } }>(
        `/projects/${projectId}/business-profile`,
        {
          ...opts.payload,
          ...(opts.force ? {} : { expectedVersion: loadedVersion }),
        }
      );
      return res.data?.data?.businessProfile;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.chatbotBusinessInfo(projectId) });
      setDirty(false);
      setConflict(null);
      toast.success('Business profile saved. The chatbot will use it on the next message.');
    },
    onError: (err) => {
      if (isVersionConflict(err)) {
        // Don't reset the draft — surface a banner and let the admin choose:
        // overwrite, or discard-and-reload.
        setConflict({ serverVersion: conflictCurrentVersion(err) ?? loadedVersion });
        return;
      }
      toast.error(apiErrorMessage(err, 'Save failed'));
    },
  });

  // Merge platform-known values (store settings, SEO config, project record)
  // into EMPTY fields of the draft only — admin-typed values always win. The
  // merge lands in the form as unsaved edits for review, never persisted here.
  const prefillMutation = useMutation({
    mutationFn: async () => {
      const res = await api.get<{ success: boolean; data: { prefill: BusinessProfile } }>(
        `/projects/${projectId}/business-profile/prefill`
      );
      return res.data?.data?.prefill ?? {};
    },
    onSuccess: (prefill) => {
      let filled = 0;
      setDraft((d) => {
        const next = { ...d };
        for (const [k, v] of Object.entries(prefill)) {
          const key = k as keyof BusinessProfile;
          const current = next[key];
          const empty =
            current == null ||
            (typeof current === 'string' && !current.trim()) ||
            (Array.isArray(current) && current.length === 0);
          if (empty && v != null) {
            (next as Record<string, unknown>)[key] = v;
            filled++;
          }
        }
        return next;
      });
      if (filled > 0) {
        setDirty(true);
        toast.success(`Filled ${filled} field${filled === 1 ? '' : 's'} — review and save.`);
      } else {
        toast.info('Nothing new to prefill — everything we know is already filled in.');
      }
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Prefill failed')),
  });

  function update<K extends keyof BusinessProfile>(key: K, value: BusinessProfile[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
    setDirty(true);
  }
  function updateSocial(key: keyof NonNullable<BusinessProfile['socials']>, value: string) {
    setDraft((d) => ({ ...d, socials: { ...(d.socials ?? {}), [key]: value } }));
    setDirty(true);
  }
  function updateLocation(idx: number, patch: Partial<Location>) {
    setDraft((d) => {
      const locs = [...(d.locations ?? [])];
      locs[idx] = { ...locs[idx], ...patch };
      return { ...d, locations: locs };
    });
    setDirty(true);
  }
  function addLocation() {
    setDraft((d) => ({
      ...d,
      locations: [...(d.locations ?? []), { name: '', address: '' }],
    }));
    setDirty(true);
  }
  function removeLocation(idx: number) {
    setDraft((d) => {
      const locs = [...(d.locations ?? [])];
      locs.splice(idx, 1);
      return { ...d, locations: locs };
    });
    setDirty(true);
  }
  function updateMultiline(key: 'additionalPhones' | 'additionalEmails', text: string) {
    const arr = text
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    update(key, arr);
  }

  function buildCleanedPayload(): BusinessProfile {
    // Strip empty strings from the payload so absent fields stay absent.
    const cleaned: BusinessProfile = {};
    for (const [k, v] of Object.entries(draft)) {
      if (typeof v === 'string' && !v.trim()) continue;
      if (Array.isArray(v) && v.length === 0) continue;
      (cleaned as any)[k] = v;
    }
    if (cleaned.socials) {
      const s = Object.fromEntries(
        Object.entries(cleaned.socials).filter(([, v]) => typeof v === 'string' && v.trim())
      );
      if (Object.keys(s).length === 0) delete cleaned.socials;
      else cleaned.socials = s as BusinessProfile['socials'];
    }
    if (cleaned.locations) {
      cleaned.locations = cleaned.locations.filter((l) => l.name.trim() && l.address.trim());
      if (cleaned.locations.length === 0) delete cleaned.locations;
    }
    return cleaned;
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    save.mutate({ payload: buildCleanedPayload() });
  }

  // Skeleton only on the first (no-cache) load; background refetches keep the
  // form visible behind a quiet "Refreshing…" hint.
  if (isPending) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="relative space-y-6">
      {isFetching && <RefreshingHint />}

      {/* ── Concurrency conflict banner ──
          Shown when a save was rejected because the profile changed on the
          server while this admin was editing. We keep their unsaved edits and
          let them choose how to reconcile. */}
      {conflict && (
        <div className="flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">
                This business profile changed elsewhere while you were editing.
              </p>
              <p className="text-amber-800">
                The server now has version {conflict.serverVersion} (you loaded {loadedVersion}).
                Your edits are still here — overwrite to keep them, or discard to load the latest.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                // Discard local edits and hydrate from the server (the seed
                // effect re-runs once dirty is cleared and fresh data lands).
                setConflict(null);
                setDirty(false);
                qc.invalidateQueries({ queryKey: qk.chatbotBusinessInfo(projectId) });
              }}
            >
              Discard &amp; load latest
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={save.isPending}
              onClick={() => save.mutate({ payload: buildCleanedPayload(), force: true })}
            >
              Overwrite with my changes
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Business Profile
              </CardTitle>
              <CardDescription>
                Authoritative contact info the chatbot will quote when customers ask about location,
                phone, email, or hours. Filling this in is the most reliable fix for
                <em> &ldquo;the bot doesn&rsquo;t know my address&rdquo;</em>. All fields optional —
                leave blank what doesn&rsquo;t apply.
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              disabled={prefillMutation.isPending}
              onClick={() => prefillMutation.mutate()}
            >
              {prefillMutation.isPending ? (
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-3 w-3" />
              )}
              Prefill from my site
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="Display name"
              hint="Customer-facing brand name"
              value={draft.displayName ?? ''}
              onChange={(v) => update('displayName', v)}
            />
            <Field
              label="Legal name"
              hint="Full legal entity (optional)"
              value={draft.legalName ?? ''}
              onChange={(v) => update('legalName', v)}
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              rows={2}
              placeholder="One or two sentences about what you do."
              value={draft.description ?? ''}
              onChange={(e) => update('description', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Primary contact</CardTitle>
          <CardDescription>The address / phone / email the bot uses by default.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Address</Label>
            <Textarea
              rows={2}
              placeholder="12, Hauz Khas Village, near the Deer Park gate, New Delhi 110016"
              value={draft.primaryAddress ?? ''}
              onChange={(e) => update('primaryAddress', e.target.value)}
            />
            <p className="text-muted-foreground mt-1 text-xs">
              Write it the way you&rsquo;d say it. The chatbot will quote it verbatim.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="Phone"
              placeholder="+91 98765 43210"
              value={draft.primaryPhone ?? ''}
              onChange={(v) => update('primaryPhone', v)}
            />
            <Field
              label="Email"
              type="email"
              placeholder="hello@yourbusiness.com"
              value={draft.primaryEmail ?? ''}
              onChange={(v) => update('primaryEmail', v)}
            />
          </div>
          <div>
            <Label>Hours</Label>
            <Input
              placeholder="Mon–Fri 9am–6pm, Sat 10–4, Sun closed"
              value={draft.hours ?? ''}
              onChange={(e) => update('hours', e.target.value)}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Additional phones</Label>
              <Textarea
                rows={2}
                placeholder="One per line"
                value={(draft.additionalPhones ?? []).join('\n')}
                onChange={(e) => updateMultiline('additionalPhones', e.target.value)}
              />
            </div>
            <div>
              <Label>Additional emails</Label>
              <Textarea
                rows={2}
                placeholder="One per line — sales@..., support@..."
                value={(draft.additionalEmails ?? []).join('\n')}
                onChange={(e) => updateMultiline('additionalEmails', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Locations / branches</CardTitle>
              <CardDescription>
                Add a row for each physical location. Phone / email / hours here override the
                primary contact for that branch.
              </CardDescription>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addLocation}>
              <Plus className="mr-1 h-4 w-4" /> Add location
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(draft.locations ?? []).length === 0 && (
            <p className="text-muted-foreground text-sm">No locations added.</p>
          )}
          {(draft.locations ?? []).map((loc, idx) => (
            <div key={idx} className="rounded-md border p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-muted-foreground text-xs">Location {idx + 1}</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeLocation(idx)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Field
                  label="Branch name"
                  placeholder="Hauz Khas"
                  value={loc.name}
                  onChange={(v) => updateLocation(idx, { name: v })}
                />
                <Field
                  label="Phone (override)"
                  value={loc.phone ?? ''}
                  onChange={(v) => updateLocation(idx, { phone: v })}
                />
              </div>
              <div className="mt-3">
                <Label>Address</Label>
                <Textarea
                  rows={2}
                  value={loc.address}
                  onChange={(e) => updateLocation(idx, { address: e.target.value })}
                />
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <Field
                  label="Email (override)"
                  type="email"
                  value={loc.email ?? ''}
                  onChange={(v) => updateLocation(idx, { email: v })}
                />
                <Field
                  label="Hours (override)"
                  value={loc.hours ?? ''}
                  onChange={(v) => updateLocation(idx, { hours: v })}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Online presence</CardTitle>
          <CardDescription>
            Public links the bot can mention. Keep blank what doesn&rsquo;t apply.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field
            label="Website"
            value={draft.socials?.website ?? ''}
            onChange={(v) => updateSocial('website', v)}
          />
          <Field
            label="Instagram"
            value={draft.socials?.instagram ?? ''}
            onChange={(v) => updateSocial('instagram', v)}
          />
          <Field
            label="Facebook"
            value={draft.socials?.facebook ?? ''}
            onChange={(v) => updateSocial('facebook', v)}
          />
          <Field
            label="Twitter / X"
            value={draft.socials?.twitter ?? ''}
            onChange={(v) => updateSocial('twitter', v)}
          />
          <Field
            label="LinkedIn"
            value={draft.socials?.linkedin ?? ''}
            onChange={(v) => updateSocial('linkedin', v)}
          />
          <Field
            label="YouTube"
            value={draft.socials?.youtube ?? ''}
            onChange={(v) => updateSocial('youtube', v)}
          />
          <Field
            label="WhatsApp"
            value={draft.socials?.whatsapp ?? ''}
            onChange={(v) => updateSocial('whatsapp', v)}
          />
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3">
        {dirty && <span className="text-muted-foreground text-sm">Unsaved changes</span>}
        <Button type="submit" disabled={!dirty || save.isPending}>
          {save.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save business profile
        </Button>
      </div>
    </form>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <Label>{props.label}</Label>
      <Input
        type={props.type ?? 'text'}
        placeholder={props.placeholder}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
      />
      {props.hint && <p className="text-muted-foreground mt-1 text-xs">{props.hint}</p>}
    </div>
  );
}
